'use client';

import { cn } from '@/lib/utils';
import { genericMemo } from '@/types/use';
import React, { useCallback, useDeferredValue, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import CustomFormItem from './form-item';
import FormItemController from './form-item-controller';
import CustomFormList from './form-list';
import {
    FormFieldsValues,
    FormItemProps,
    FormProps,
    FormProviderProps,
    RenderFormItemProps,
} from './form-types';

const Form = genericMemo(<T extends FormFieldsValues>(props: FormProps<T>) => {
    const {
        rls,
        children,
        className,
        useFormTag = false,
        wrapperClassName,
        layout = 'vertical',
    } = props;

    const _loop = useCallback(
        (data: FormItemProps<T>) => {
            return data.map(
                (
                    {
                        render,
                        renderFieldArray,
                        name,
                        type: itemType,
                        hidden,
                        ...rest
                    },
                    idx
                ) => {
                    const _key = name ?? idx;

                    if (hidden) return <React.Fragment key={_key} />;
                    if (
                        itemType === 'Grid' ||
                        itemType === 'Flex' ||
                        itemType === 'Block'
                    ) {
                        const isFormItemProps = (
                            child: any
                        ): child is FormItemProps<T> => {
                            return (
                                Array.isArray(child) &&
                                typeof child?.[0] === 'object'
                            );
                        };
                        if (isFormItemProps(rest.children)) {
                            let _rootCls = 'grid grid-cols-2 gap-x-2'; // default grid
                            if (itemType === 'Flex') {
                                _rootCls = 'flex items-center gap-x-2'; // flex class
                            } else if (itemType === 'Block') {
                                _rootCls = 'mb-2'; // block
                            }

                            const child = (
                                <div
                                    className={cn(
                                        _rootCls,
                                        rest.wrapperClassName
                                    )}
                                >
                                    {_loop(rest.children as FormItemProps<T>)}
                                </div>
                            );

                            return (
                                <React.Fragment key={_key}>
                                    {typeof rest.renderParent === 'function'
                                        ? rest.renderParent(child)
                                        : child}
                                </React.Fragment>
                            );
                        }
                    } else if (itemType === 'Custom') {
                        return (
                            <React.Fragment key={_key}>
                                {rest.renderParent?.(<></>)}
                            </React.Fragment>
                        );
                    } else {
                        const isRenFormItem = !!(name || rest.renderParent);
                        return isRenFormItem ? (
                            <RenderFormItem
                                key={_key}
                                name={name}
                                render={render}
                                renderFieldArray={renderFieldArray}
                                type={itemType}
                                layout={layout}
                                {...(rest as any)}
                            />
                        ) : (
                            <React.Fragment key={_key} />
                        );
                    }
                }
            );
        },
        [layout]
    );

    const _child = useMemo(() => {
        return _loop(rls);
    }, [_loop, rls]);

    if (!rls) {
        return <React.Fragment />;
    }

    if (useFormTag) {
        return (
            <form id='RHF-Custom' className={cn('block', wrapperClassName)}>
                <div className={cn('rls', className)}>{_child}</div>
                {children && <div className='RHF-child'>{children}</div>}
            </form>
        );
    }

    return (
        <div id='RHF-Custom' className={cn('block', wrapperClassName)}>
            <div className={cn('rls', className)}>{_child}</div>
            {children && <div className='RHF-child'>{children}</div>}
        </div>
    );
});

const RenderFormItem = genericMemo(
    <T extends FormFieldsValues>(props: RenderFormItemProps<T>) => {
        const {
            type,
            name,
            render,
            renderParent,
            children,

            // pass child form FormControllerItem
            ...rest
        } = props;

        const _renderItem = useMemo(() => {
            return (
                <CustomFormItem<T>
                    name={name}
                    render={({ field, fieldState, formState }) => {
                        // render default của item
                        let clonedItem = React.isValidElement(children)
                            ? React.cloneElement(children, field)
                            : null;

                        //   if custom render input control
                        if (typeof render === 'function') {
                            clonedItem = render({
                                field,
                                fieldState,
                                formState,
                            });
                        }

                        return (
                            <FormItemController {...rest}>
                                {clonedItem}
                            </FormItemController>
                        );
                    }}
                />
            );
        }, [children, name, render, rest]);

        switch (type) {
            case 'FieldArray': {
                return typeof renderParent === 'function' ? (
                    renderParent(<RenderFormFieldArray {...(props as any)} />)
                ) : (
                    <RenderFormFieldArray {...(props as any)} />
                );
            }
            case 'Item':
            default:
                return typeof renderParent === 'function'
                    ? renderParent(_renderItem)
                    : _renderItem;
        }
    }
);

const RenderFormFieldArray = genericMemo(
    <T extends FormFieldsValues>({
        name,
        renderFieldArray,
        wrapperClassName,
    }: RenderFormItemProps<T>) => {
        if (typeof renderFieldArray !== 'function') {
            return <React.Fragment />;
        }
        return (
            <CustomFormList<T> name={name} className={wrapperClassName}>
                {renderFieldArray}
            </CustomFormList>
        );
    }
);

const Provider = genericMemo(
    <T extends FormFieldsValues = any>({
        children,
        ...methods
    }: FormProviderProps<T>) => {
        const _chidren = useDeferredValue(children);
        return <FormProvider {...methods}>{_chidren}</FormProvider>;
    }
);

export { Provider };
export default genericMemo(Form);
